import { useState, useEffect, useCallback, useRef } from 'react';
import { getSupabaseClient } from '@/lib/supabase';
import type { User } from '@supabase/supabase-js';

// Build redirect URL with base path prefix so email confirmation & OAuth
// redirect back to the correct project URL (e.g. /project/{id}/sign-in).
const basePath = __BASE_PATH__.split('/').filter(Boolean).join('/');
const pathPrefix = basePath ? `/${basePath}` : '';
const signInRedirectUrl = `${window.location.origin}${pathPrefix}/sign-in`;

export interface Profile {
  id: string;
  email: string | null;
  full_name: string | null;
  phone: string | null;
  avatar_url: string | null;
  role: string;
  provider: string | null;
  locale: string | null;
  default_currency: string | null;
  created_at: string;
  updated_at: string | null;
}

export interface Company {
  id: string;
  owner_user_id: string;
  company_name: string | null;
  country: string | null;
  business_type: string | null;
  website: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  tax_id: string | null;
  company_size: string | null;
  status: string;
  created_at: string;
  updated_at: string | null;
}

export interface AuthState {
  user: User | null;
  profile: Profile | null;
  company: Company | null;
  isLoading: boolean;
  isProfileLoading: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
}

// Detect the "Invalid Refresh Token" failure that happens when the locally
// persisted session has expired / been revoked and Supabase cannot refresh it.
function isInvalidRefreshTokenError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const message = (error as { message?: string }).message || '';
  return (
    message.includes('Invalid Refresh Token') ||
    message.includes('Refresh Token Not Found')
  );
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    profile: null,
    company: null,
    isLoading: true,
    isProfileLoading: true,
    isAdmin: false,
    isSuperAdmin: false,
  });

  const refreshingRef = useRef<string | null>(null);

  const refreshProfile = useCallback(async (userId: string) => {
    // Prevent concurrent refresh for the same user
    if (refreshingRef.current === userId) return;
    refreshingRef.current = userId;

    try {
      const supabase = getSupabaseClient();
      if (!supabase) return;

      // 1) Fetch existing profile
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching profile:', error);
        return;
      }

      let profile: Profile | null = data as Profile | null;

      // 2) Auto-create profile if missing — use upsert to avoid race-condition duplicate key errors
      if (!profile) {
        let user: User | null = null;
        try {
          const { data: userData } = await supabase.auth.getUser();
          user = userData?.user ?? null;
        } catch (getUserError) {
          console.error('Error getting user:', getUserError);
          // If the refresh token is dead, sign out locally so the user can log in again.
          if (isInvalidRefreshTokenError(getUserError)) {
            supabase.auth.signOut({ scope: 'local' }).catch(() => {});
          }
          return;
        }

        if (!user) return;

        const newProfile = {
          id: user.id,
          email: user.email,
          full_name:
            user.user_metadata?.full_name ||
            user.user_metadata?.name ||
            null,
          phone: null,
          avatar_url: null,
          role: 'customer',
          provider: user.app_metadata?.provider || 'email',
          locale: user.user_metadata?.locale || null,
          default_currency: 'USD',
        };

        const { data: upserted, error: upsertErr } = await supabase
          .from('profiles')
          .upsert(newProfile, { onConflict: 'id' })
          .select()
          .maybeSingle();

        if (upsertErr) {
          console.error('Error auto-creating profile:', upsertErr);
          return;
        }

        if (upserted) {
          profile = upserted as Profile;
        }
      }

      // 3) Fetch company info if profile exists
      let company: Company | null = null;
      if (profile) {
        const { data: companyData, error: companyError } = await supabase
          .from('customer_companies')
          .select('*')
          .eq('owner_user_id', userId)
          .maybeSingle();

        if (companyError) {
          console.error('Error fetching company:', companyError);
        } else if (companyData) {
          company = companyData as Company;
        }

        setState((prev) => ({
          ...prev,
          profile,
          company,
          isAdmin: profile.role === 'admin' || profile.role === 'super_admin',
          isSuperAdmin: profile.role === 'super_admin',
        }));
      }
    } finally {
      refreshingRef.current = null;
      setState((prev) => ({ ...prev, isProfileLoading: false }));
    }
  }, []);

  useEffect(() => {
    const supabase = getSupabaseClient();
    if (!supabase) {
      setState((prev) => ({ ...prev, isLoading: false, isProfileLoading: false }));
      return;
    }

    // Get initial session. If the persisted refresh token is no longer valid,
    // clear it locally so the user can sign in again instead of getting stuck.
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        const user = session?.user ?? null;
        if (user) {
          setState((prev) => ({ ...prev, user, isLoading: false }));
          refreshProfile(user.id);
        } else {
          setState((prev) => ({ ...prev, user, isLoading: false, isProfileLoading: false }));
        }
      })
      .catch((err) => {
        console.error('Error getting session:', err);
        if (isInvalidRefreshTokenError(err)) {
          supabase.auth.signOut({ scope: 'local' }).catch(() => {});
        }
        setState((prev) => ({ ...prev, isLoading: false }));
      });

    // Listen for auth changes
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      const user = session?.user ?? null;
      setState((prev) => ({ ...prev, user }));
      if (user) {
        refreshProfile(user.id);
      } else {
        setState((prev) => ({
          ...prev,
          profile: null,
          company: null,
          isAdmin: false,
          isSuperAdmin: false,
          isProfileLoading: false,
        }));
      }
    });

    return () => {
      listener.subscription.unsubscribe();
    };
  }, [refreshProfile]);

  const signIn = async (email: string, password: string) => {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;
    return data;
  };

  const signUp = async (email: string, password: string, meta?: { full_name?: string }) => {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: meta,
        emailRedirectTo: signInRedirectUrl,
      },
    });

    if (error) throw error;
    return data;
  };

  const signInWithOAuth = async (provider: 'google' | 'facebook') => {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: signInRedirectUrl,
      },
    });

    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    const supabase = getSupabaseClient();
    if (!supabase || !state.user) throw new Error('Not authenticated');

    // Prevent role changes from customer side
    const safeUpdates = { ...updates };
    delete (safeUpdates as Record<string, unknown>).role;
    delete (safeUpdates as Record<string, unknown>).id;

    const { data, error } = await supabase
      .from('profiles')
      .update(safeUpdates)
      .eq('id', state.user.id)
      .select()
      .maybeSingle();

    if (error) throw error;
    if (data) {
      setState((prev) => ({ ...prev, profile: data as Profile }));
    }
    return data;
  };

  const updateCompany = async (updates: Partial<Company>) => {
    const supabase = getSupabaseClient();
    if (!supabase || !state.user) throw new Error('Not authenticated');

    const safeUpdates = { ...updates };
    delete (safeUpdates as Record<string, unknown>).id;
    delete (safeUpdates as Record<string, unknown>).owner_user_id;

    if (state.company) {
      // Update existing company
      const { data, error } = await supabase
        .from('customer_companies')
        .update(safeUpdates)
        .eq('id', state.company.id)
        .eq('owner_user_id', state.user.id)
        .select()
        .maybeSingle();

      if (error) throw error;
      if (data) {
        setState((prev) => ({ ...prev, company: data as Company }));
      }
      return data;
    }

    // Create new company
    const newCompany = {
      owner_user_id: state.user.id,
      ...safeUpdates,
      status: 'active',
    };

    const { data, error } = await supabase
      .from('customer_companies')
      .insert(newCompany)
      .select()
      .maybeSingle();

    if (error) throw error;
    if (data) {
      setState((prev) => ({ ...prev, company: data as Company }));
    }
    return data;
  };

  return {
    ...state,
    signIn,
    signUp,
    signInWithOAuth,
    signOut,
    updateProfile,
    updateCompany,
    refreshProfile,
  };
}